import React from "react";
import { Phone, Mail, MessageCircle } from "lucide-react";
import {
  trackPhoneClick,
  trackEmailClick,
  trackWhatsAppClick,
} from "../lib/tracking";

// Public contact endpoints
export const PHONE_INTL = "+33602474620"; // E.164
export const PHONE_DISPLAY = "+33 6 02 47 46 20";
export const EMAIL = "architecte@kerkoub.fr";
export const EMAIL_PRO = "kerkoub_abdelkrim@architectes.org";
export const EMAIL_SUBJECT = "Demande de devis architecture BIM";
export const WHATSAPP_NUMBER = "33602474620"; // sans le +
export const WHATSAPP_MESSAGE =
  "Bonjour Karim, je souhaite vous contacter pour un projet architecture / BIM / Revit.";

export const TEL_HREF = `tel:${PHONE_INTL}`;
export const MAIL_HREF = `mailto:${EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`;
export const MAIL_PRO_HREF = `mailto:${EMAIL_PRO}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`;
export const WA_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  WHATSAPP_MESSAGE
)}`;

/**
 * QuickActions — reusable CTA bar with Phone / Email / WhatsApp.
 * variant: "header" (compact icons) | "inline" (full pill buttons) | "footer"
 */
export default function QuickActions({
  variant = "inline",
  source = "site",
  showLabels = true,
  className = "",
}) {
  if (variant === "header") {
    return (
      <div
        data-testid="quick-actions-header"
        className={`hidden md:flex items-center gap-1 border border-[#E5E5E5] px-1.5 py-1 ${className}`}
      >
        <IconLink
          href={TEL_HREF}
          label="Appeler"
          testid={`qa-phone-${variant}`}
          onClick={() => trackPhoneClick(source)}
          icon={<Phone size={14} />}
        />
        <span className="text-[#CCC] text-xs">|</span>
        <IconLink
          href={MAIL_HREF}
          label="Email"
          testid={`qa-email-${variant}`}
          onClick={() => trackEmailClick(source)}
          icon={<Mail size={14} />}
        />
        <span className="text-[#CCC] text-xs">|</span>
        <IconLink
          href={WA_HREF}
          label="WhatsApp"
          external
          testid={`qa-whatsapp-${variant}`}
          onClick={() => trackWhatsAppClick(source)}
          icon={<MessageCircle size={14} />}
        />
      </div>
    );
  }

  if (variant === "footer") {
    return (
      <div
        data-testid="quick-actions-footer"
        className={`flex flex-wrap items-center gap-3 ${className}`}
      >
        <PillButton
          href={TEL_HREF}
          label="Appeler"
          testid={`qa-phone-${variant}`}
          onClick={() => trackPhoneClick(source)}
          icon={<Phone size={14} />}
          variant="dark"
        />
        <PillButton
          href={MAIL_HREF}
          label="Email"
          testid={`qa-email-${variant}`}
          onClick={() => trackEmailClick(source)}
          icon={<Mail size={14} />}
          variant="dark"
        />
        <PillButton
          href={WA_HREF}
          label="WhatsApp"
          testid={`qa-whatsapp-${variant}`}
          onClick={() => trackWhatsAppClick(source)}
          icon={<MessageCircle size={14} />}
          external
          variant="dark"
        />
      </div>
    );
  }

  // inline — default
  return (
    <div
      data-testid="quick-actions-inline"
      className={`flex flex-wrap items-center gap-3 ${className}`}
    >
      <PillButton
        href={TEL_HREF}
        label={showLabels ? "Appeler" : ""}
        testid={`qa-phone-${variant}`}
        onClick={() => trackPhoneClick(source)}
        icon={<Phone size={16} />}
      />
      <PillButton
        href={MAIL_HREF}
        label={showLabels ? "Envoyer un email" : ""}
        testid={`qa-email-${variant}`}
        onClick={() => trackEmailClick(source)}
        icon={<Mail size={16} />}
      />
      <PillButton
        href={WA_HREF}
        label={showLabels ? "WhatsApp" : ""}
        testid={`qa-whatsapp-${variant}`}
        onClick={() => trackWhatsAppClick(source)}
        icon={<MessageCircle size={16} />}
        external
        variant="whatsapp"
      />
    </div>
  );
}

function IconLink({ href, label, icon, onClick, external, testid }) {
  return (
    <a
      data-testid={testid}
      href={href}
      onClick={onClick}
      aria-label={label}
      title={label}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      className="w-7 h-7 flex items-center justify-center text-[#0A0A0A] hover:text-[#D35400] transition-colors"
    >
      {icon}
    </a>
  );
}

function PillButton({ href, label, icon, onClick, external, testid, variant }) {
  const base =
    "group inline-flex items-center gap-2 px-4 py-2.5 text-xs md:text-sm tracking-wide transition-colors duration-500 font-mono uppercase";
  let cls = "";
  if (variant === "whatsapp") {
    cls = `${base} bg-[#25D366] text-white hover:bg-[#1da851]`;
  } else if (variant === "dark") {
    cls = `${base} border border-[#F7F7F9]/40 text-[#F7F7F9] hover:bg-[#D35400] hover:border-[#D35400]`;
  } else {
    cls = `${base} border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F7F7F9]`;
  }
  return (
    <a
      data-testid={testid}
      href={href}
      onClick={onClick}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      className={cls}
    >
      {icon}
      {label}
    </a>
  );
}
